import { Box, IconButton, List, Typography } from '@mui/material';
import { FlexContainer } from './FlexContainer';
import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { IconLibrary } from '../../display/IconLibrary/IconLibrary';
import { useDisplayModeContext } from '../../theme/display';
import LanguageSelector from '@act/react-components/consents/member-view/LanguageSelector';
import { useMobile } from '@act/utilities/mobile-context';

interface SideMenuProps {
  children?: React.ReactNode;
  menuTitle?: string;
  minWidth?: string;
  listOptions?: {
    gap?: number;
  };
  notificationMenu?: React.ReactNode;
  subMenu?: React.ReactElement;
}

export const SideMenu = ({
  children,
  menuTitle,
  minWidth,
  listOptions,
  notificationMenu,
  subMenu,
}: SideMenuProps) => {
  const theme = useTheme();
  const { displayMode, setDisplayMode } = useDisplayModeContext();
  const { isMobile } = useMobile();

  const handleDisplayModeToggle = () => {
    setDisplayMode(displayMode === 'compact' ? 'expanded' : 'compact');
  };

  // Reset to expanded mode when switching to mobile
  useEffect(() => {
    if (isMobile && displayMode === 'compact') {
      setDisplayMode('expanded');
    }
  }, [isMobile, displayMode, setDisplayMode]);

  let gap;

  if (isMobile) {
    gap = 0;
  } else {
    gap = listOptions?.gap ?? 1;
  }

  return (
    <FlexContainer
      direction="column"
      padding={isMobile ? 0 : '24px 0 16px'}
      alignItems="center"
      justifyContent="space-between"
      minWidth={minWidth || 'auto'}
      height="100%"
      overflow="hidden"
      noWrap
      sx={{
        borderRight: isMobile
          ? 'none'
          : `1px solid ${theme.palette.strokePrimary}`,
      }}
    >
      <Box
        sx={{
          padding: isMobile ? '0' : '0 16px',
          width: '100%',
          boxSizing: 'border-box',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Typography variant="headlineSmall">{menuTitle}</Typography>
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap,
            width: '100%',
          }}
        >
          {children}
        </List>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: isMobile ? '8px' : '16px',
          borderTop: `1px solid ${theme.palette.strokePrimary}`,
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        {isMobile && <LanguageSelector />}

        {notificationMenu && notificationMenu}

        {subMenu && subMenu}

        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <IconButton color="secondary" onClick={handleDisplayModeToggle}>
              {displayMode === 'compact' ? (
                <IconLibrary
                  iconName="ArrowLineRight"
                  size={32}
                  weight="regular"
                />
              ) : (
                <IconLibrary
                  iconName="ArrowLineLeft"
                  size={32}
                  weight="regular"
                />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </FlexContainer>
  );
};
